import { useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import { teacherApi } from '../../lib/teacherApi';
import { isProcessingItem } from '../../lib/sessionStatus';

/**
 * Deskey Sessions list for Content Studio, 50 at a time.
 * Backend: GET /api/teacher/audio-inbox?skip&limit&tab&q (audio_inbox_routes.list_audio_inbox)
 * searches and filters ALL the teacher's sessions, then pages the result.
 */
export const INBOX_KEY = ['teacher', 'audio-inbox'];
export const INBOX_PAGE_SIZE = 50;
const INBOX_POLL_MS = 4000;
const LOAD_AHEAD_PX = 300;

/** Every loaded page in one list; a session that shifted pages while new ones synced shows once. */
export function flatInboxItems(data) {
  const seen = new Set();
  const items = [];
  for (const page of data?.pages || []) {
    for (const item of page.items) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      items.push(item);
    }
  }
  return items;
}

/** Offset of the next page, or undefined when the server said there are no more. */
export function nextInboxSkip(lastPage, pages) {
  return lastPage.has_more ? pages.length * INBOX_PAGE_SIZE : undefined;
}

/** Sessions on Studio tab `tab` matching search `q`. Polls while any loaded session is still processing. */
export function useInboxPages(tab, q) {
  const query = useInfiniteQuery({
    queryKey: [...INBOX_KEY, tab, q],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({
        include_skipped: 'true', limit: String(INBOX_PAGE_SIZE), skip: String(pageParam), tab, q,
      });
      const r = await teacherApi(`/audio-inbox?${params}`);
      if (!r?.items) throw new Error('inbox unavailable'); // keeps the last list on screen
      return r;
    },
    initialPageParam: 0,
    getNextPageParam: nextInboxSkip,
    placeholderData: keepPreviousData, // old list stays up while a new search loads
    meta: { silent: true },
    refetchInterval: (qy) => (flatInboxItems(qy.state.data).some(isProcessingItem) ? INBOX_POLL_MS : false),
  });
  const items = useMemo(() => flatInboxItems(query.data), [query.data]);
  return { ...query, items };
}

/** Ref for an element at the end of the list; seeing it loads the next page. */
export function useLoadMoreOnScroll({ hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    // Stop on error so a failing page isn't requested in a loop; the Retry button takes over.
    if (!el || !hasNextPage || isFetchingNextPage || isFetchNextPageError) return undefined;
    // A fresh observer reports right away, so a page that didn't fill the screen loads the next one too.
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) fetchNextPage();
    }, { rootMargin: `${LOAD_AHEAD_PX}px` });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);
  return ref;
}
